import { useMemo } from 'react';

import { UseTableFiltersReturn } from '@/components/data-table/filters-v2';

import usePlannerStore from '../store';

/**
 * Adapts the planner's Zustand-backed filter state to the V2 `useTableFilters`
 * interface, so the planner can drive `DataTableFiltersV2` in controlled mode
 * without moving its filter state into URL search params.
 *
 * The planner store already persists filters (URL + localStorage) and runs
 * viewport/scroll-preservation on every change, so all mutations route through
 * `setFilters` / `resetFilter` rather than touching search params.
 */
export const usePlannerFilterState = (): UseTableFiltersReturn => {
  const filters = usePlannerStore(store => store.filters);
  const setFilters = usePlannerStore(store => store.setFilters);
  const resetFilter = usePlannerStore(store => store.resetFilter);

  return useMemo(() => {
    const getFilterValue = (name: string): string[] => {
      const value = filters[name];
      if (!value) return [];
      return Array.isArray(value) ? value : [value];
    };

    const isNonEmpty = (value: string | string[]): boolean =>
      Array.isArray(value) ? value.length > 0 : value.trim() !== '';

    const activeFiltersCount = Object.values(filters).filter(isNonEmpty).length;

    return {
      activeFilters: filters,
      activeFiltersCount,
      hasActiveFilters: activeFiltersCount > 0,
      setFilter: (name, value) => setFilters(name, value),
      // Match the V1 planner behaviour: clearing a single filter stores an empty
      // array (the assignment mapper treats it the same as absent).
      clearFilter: name => setFilters(name, []),
      clearAllFilters: () => resetFilter(),
      applyFilters: next => {
        Object.entries(next).forEach(([name, value]) => setFilters(name, value));
      },
      removeFilterValue: (name, valueToRemove) => {
        if (!valueToRemove) {
          setFilters(name, []);
          return;
        }
        const current = getFilterValue(name);
        setFilters(
          name,
          current.filter(v => v !== valueToRemove)
        );
      },
      getFilterValue,
    };
  }, [filters, setFilters, resetFilter]);
};
